/*
 * Read the "Troubleshooting" section in here: https://www.npmjs.com/package/react-tooltip
 * before using `<Tooltip/>` within the `<ForaModal/>` component:
 */

import { Tooltip as ReactTooltip } from 'react-tooltip';
import { css, styled } from 'styled-components';
import { ReactNode } from 'react';

import { theme } from '../../styles';
import { useTabbing } from '../../utils';
import { Text } from '../Text';
import { useIsDeviceSize } from '../Media';

const Placeholder = styled.button<{ isTabbing: boolean; variant: string }>`
  background: none;
  border: none;
  padding: 0;
  appearance: none;
  outline: none;

  /* ${theme.typography.root}; */
  /** inherit font props from parent */
  font-size: inherit;
  line-height: inherit;
  font-weight: inherit;
  font-family: inherit;
  color: inherit;
  display: inline-flex;
  cursor: pointer;
  ${props =>
    props.isTabbing &&
    css`
      &:focus {
        outline: ${theme.colors.darkBlue} solid 2px;
        outline-offset: 2px;
      }
    `}
  ${props =>
    props.variant === 'text' &&
    css`
      background-position: 1px bottom;
      background-repeat: repeat-x;
      background-size: 6px 1px;
      background-image: linear-gradient(to right, ${theme.colors.black90} 50%, transparent 0%);
      line-height: 1;
    `}
`;

type TooltipSize = 'standard' | 'small';

type StyledTooltipProps = {
  size: TooltipSize;
  isInTable: boolean;
};

const StyledTooltip = styled(ReactTooltip)<StyledTooltipProps>`
  background-color: ${theme.colors.babyBlue} !important;
  opacity: 1 !important;
  border-radius: 2px;
  box-sizing: border-box;
  width: ${props => (props.size === 'small' ? 'min(300px, 100%)' : 'min(335px, 100%)')}!important;
  padding: ${props => (props.size === 'small' ? '0.625rem 0.75rem ' : '1.25rem 1.25rem 1.875rem 1.25rem')} !important;
  z-index: 1;
  ${props =>
    !props.isInTable &&
    css`
      /** Full width on mobile */
      @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
        left: 0 !important;
        width: 100%;
        margin-right: 1.25rem !important;
        margin-left: 1.25rem !important;
        padding: 1.25rem 1.25rem 1.875rem 1.25rem !important;
      }
    `}

  .arrow {
    /*
    *  Tooltip design in Figma: https://www.figma.com/file/p7y7LjpsfOzLqlodfElrSu/Fora-Styles-%26-components?type=design&node-id=1314-9134&mode=dev
    *  The react-tooltip arrow/triangle is actually square that is turned 45 degrees (diamond shape) which is an arrow pointing in all directions.
    *  To adjust it to a 22px wide triangle as per Fora's design, we need to scale the default square.
    *  (simply changing the width/height won't do since it distrupts react-tooltip's positioning calculation)
    *
    *  The square's original dimensions are 8 * 8 px. The diagonal of a square is d = a√2. a=8*x (x is our sought multiplier).
    *  To get a 22px diagonal: 22 = 8x√2 => x = 22/(8√2) => x ≈ 1.9445
    */
    transform: rotate(45deg) scale(1.9445);
  }
`;

const StyledLink = styled.a`
  color: black;
`;

type TooltipProps = {
  /** id to open the Tooltip, and to add a data-testid */
  id: string;
  /** The title/label that is meant to show on the content of the tooltip */
  contentLabel?: string;
  /** Descriptive text to be shown inside the Tooltip */
  content?: string | React.ReactNode;
  /** If set to `text` then the Tooltip "placeholder" will have a bottom border, otherwise it won't   */
  variant?: 'text' | 'object';
  /** Toggle styling if the tooltip is used in a table  */
  isInTable?: boolean;
  linkUrl?: string;
  linkText?: string;
  /** Set true true to toggle open on hover instead of click */
  openOnHover?: boolean;
  /** Controls fontsize and padding */
  size?: TooltipSize;
  /** Stops an event from being passed on to other elements */
  stopPropagation?: boolean;
  /** Insert text or object as a placeholder  */
  children: ReactNode;
};

export const Tooltip = ({
  id,
  variant = 'text',
  contentLabel,
  content,
  isInTable = false,
  linkUrl,
  linkText,
  openOnHover = false,
  size = 'standard',
  stopPropagation = false,
  children,
}: TooltipProps) => {
  const isTabbing = useTabbing();
  const { isMobile } = useIsDeviceSize();

  return (
    <>
      <Placeholder
        {...(stopPropagation ? { onClick: (e: React.MouseEvent<HTMLElement>) => e.stopPropagation() } : {})}
        variant={variant}
        data-tooltip-id={id}
        data-testid={`test-${id}`}
        isTabbing={isTabbing}
      >
        {children}
      </Placeholder>
      <StyledTooltip
        id={id}
        wrapper="span"
        place="bottom"
        isInTable={isInTable}
        data-testid={`test-tooltip-${id}`}
        clickable={!!linkUrl}
        openOnClick={isMobile ? true : !openOnHover}
        classNameArrow="arrow"
        size={size}
        /** Default offset is 10, add difference in height between custom and default arrow: 10 + (11 - ((8√2)/2)) ≈ 15.34 */
        offset={15.34}
      >
        {contentLabel && (
          <Text
            size={size}
            color="black90"
            fontWeight="semibold"
            renderAs="span"
            marginBottom={2}
            style={{ display: 'block' }}
            textAlign="left"
          >
            {contentLabel}
          </Text>
        )}
        {(content && typeof content === 'string') || linkUrl ? (
          <Text
            size={size}
            color="black90"
            renderAs="span"
            style={{ display: 'block' }}
            whiteSpace="pre-line"
            textAlign="left"
          >
            {content}
            {linkUrl && (
              <>
                {' '}
                <StyledLink target="_blank" href={linkUrl}>
                  {linkText}
                </StyledLink>
              </>
            )}
          </Text>
        ) : (
          content
        )}
      </StyledTooltip>
    </>
  );
};
